import React from 'react';
import { ArrowRight, Target, Shield, Zap, Users, Globe, Building2, TrendingUp, Heart, MapPin, Phone, Mail } from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import ProductRoadmap from '../components/ProductRoadmap';

const About: React.FC = () => {
  const values = [
    {
      icon: Shield,
      title: "Secure",
      description:
        "Bank-grade security and data protection. Your fleet data is always safe with enterprise-level encryption and compliance standards.",
      color: "bg-blue-50 text-blue-600",
    },
    {
      icon: Zap,
      title: "Scalable",
      description:
        "From 10 vehicles to 10,000+, our platform grows with you. Same powerful features, regardless of fleet size.",
      color: "bg-orange-50 text-orange-600",
    },
    {
      icon: Target,
      title: "Adaptive",
      description:
        "Intuitive design means no training required. Your team can be productive from day one with our user-friendly interface.",
      color: "bg-green-50 text-green-600",
    },
  ];


  const leadership = [
    {
      name: 'Jayaprasad (JP) Rao',
      role: 'Chief Executive Officer',
      bio: '20+ years in Banking and Digital Transformation. Building scalable, low-code platforms for financial operations and ventures.',
      image: '/team/Jayaprasad.png'
    },
    {
      name: 'Prasad Kande',
      role: 'Chief Operating Officer',
      bio: 'Former CEO of Telvi Technologies with 15+ years building fleet and logistics tech solutions. Leads GaadiMatic end-to-end operations — from fleet operator onboarding to customer success and delivery excellence.',
      image: '/team/prasadkande.png'
    },
    {
      name: 'Swamy Sirigada',
      role: 'Chief Financial Officer',
      bio: '18+ years in finance and mobility. Founder of Phoenitech Travels. Advocate for technology-led transformation in logistics.',
      image: '/team/swamy.png'
    },
    {
      name: 'Srinivasulu ',
      role: 'Chief Technology Officer',
      bio: '14+ years spanning Java, Python, PHP, React, and enterprise DevOps. Security-first engineering architect who builds high-availability systems that keep fleet operations running without downtime.',
      image: '/team/srinivasulu.png'
    },
  ];

  const stats = [
    { value: '500+', label: 'Fleet Operators', icon: Building2 },
    { value: '20+', label: 'Technical Team', icon: Users },
    { value: '15', label: 'Product Lines', icon: Target },
    { value: '15-20%', label: 'Avg. Cost Savings', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-white">
      <AnimatedBackground />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden bg-gray-50">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-accent-orange/5" />
        <div className="container-main relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
              <Heart className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">
                Built with passion in India
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-950 leading-tight mb-6">
              Building the Future of{" "}
              <span className="text-primary-600">Fleet Management</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
              We're on a mission to transform how Indian fleet operators manage
              their vehicles, drivers, and operations—bringing enterprise-grade
              technology within reach of every fleet, big or small.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container-main">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-6 h-6 text-primary-600" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-navy-950 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  GaadiMatic Technologies was born out of a simple yet powerful
                  belief: Indian fleet operators deserve world-class technology
                  that speaks to their unique challenges.
                </p>
                <p>
                  After experiencing firsthand the chaos of managing fleets
                  through spreadsheets, endless phone calls, and paper logs, our
                  founding team set out to create something transformative—a
                  solution that goes beyond just another tracking tool. What we
                  needed was a comprehensive operating system for fleet
                  management, designed to bring clarity, efficiency, and
                  control.
                </p>
                <p>And that's how GaadiMatic FleetX came to life.</p>
                <p>
                  From our humble beginnings in Hyderabad, we've grown into a
                  platform trusted by fleet operators across India. Our solution
                  now powers a wide range of operations, from employee transport
                  to long-haul trucking and even complex supply chains. What
                  started as a startup has evolved into a trusted partner for
                  businesses of all sizes—whether SMBs or large enterprises.
                </p>
                <p>
                  Today, our dedicated team of 20+ engineers, designers, and
                  logistics experts work tirelessly to simplify fleet
                  management. Our mission is clear:{" "}
                  <strong>
                    Make fleet operations so easy, you'll wonder how you ever
                    managed without us.
                  </strong>
                </p>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary-600" />
                  <span className="text-gray-700 font-medium">
                    Hyderabad, India
                  </span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-primary-50 to-accent-orange/5 rounded-2xl p-8 sm:p-12">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Globe className="w-10 h-10 text-white" />
                  </div>
                  <blockquote className="text-xl sm:text-2xl font-medium text-navy-950 italic mb-6">
                    "From chaos to control—that's the promise we make to every
                    fleet operator who trusts us with their business."
                  </blockquote>
                  <div className="font-semibold text-gray-900">
                    Swamy Sirigada
                  </div>
                  <div className="text-sm text-gray-500">
                    Chief Financial Officer
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600">
              These three principles guide everything we build and every
              decision we make.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="card p-8">
                <div
                  className={`w-14 h-14 ${value.color} rounded-xl flex items-center justify-center mb-6`}
                >
                  <value.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-navy-950 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Roadmap Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-4">
              <Target className="w-4 h-4 text-primary-600" />
              <span className="text-sm font-medium text-primary-700">
                Platform Vision
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-gray-600">
              A clear roadmap for expanding our AI-powered fleet management
              platform — from school transport to last-mile delivery and beyond.
            </p>
          </div>

          <ProductRoadmap />
        </div>
      </section>

      {/* Leadership Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-main">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-navy-950 mb-4">
              Meet Our Leadership
            </h2>
            <p className="text-lg text-gray-600">
              Experienced leaders driving innovation in fleet management
              technology.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {leadership.map((leader, index) => (
              <div
                key={index}
                className="card overflow-hidden group hover:shadow-xl transition-shadow duration-300"
              >
                <div className="h-24 bg-gradient-to-br from-primary-600 to-primary-800" />
                <div className="px-6 pb-8">
                  <div className="relative -mt-12 mb-4">
                    <div className="w-24 h-24 bg-white rounded-full p-1 mx-auto overflow-hidden shadow-md group-hover:scale-105 transition-transform duration-300">
                      <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center overflow-hidden">
                        {leader.image.startsWith("/") ? (
                          <img
                            src={leader.image}
                            alt={leader.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-2xl font-bold text-primary-700">
                            {leader.image}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-navy-950 mb-1">
                      {leader.name}
                    </h3>
                    <p className="text-primary-600 text-sm font-medium mb-4">
                      {leader.role}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">
                      {leader.bio}
                    </p>
                    <div className="mt-6 flex justify-center gap-3">
                      <a
                        href="https://linkedin.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-9 h-9 bg-gray-50 hover:bg-primary-50 rounded-full flex items-center justify-center transition-colors"
                      >
                        <svg
                          className="w-4 h-4 text-gray-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                        </svg>
                      </a>
                      <a
                        href={`mailto:info@gaadimatic.com`}
                        className="w-9 h-9 bg-gray-50 hover:bg-primary-50 rounded-full flex items-center justify-center transition-colors"
                      >
                        <Mail className="w-4 h-4 text-gray-600" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Backed by a team of 20+ engineers, designers, and logistics
              experts
            </p>
            <div className="flex items-center justify-center gap-2 text-primary-600">
              <Users className="w-5 h-5" />
              <span className="font-medium">Join our growing team</span>
            </div>
          </div>
        </div>
      </section>

      {/* Careers CTA */}
      <section className="section-padding bg-primary-600">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Join Our Team
            </h2>
            <p className="text-lg text-primary-100 mb-8">
              We're always looking for talented individuals who are passionate
              about transforming the logistics industry. If you're excited about
              building world-class products for Indian fleet operators, we'd
              love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-primary-600 bg-white rounded-lg transition-all duration-200 hover:bg-gray-100">
                View Open Positions
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
              <a
                href="mailto:careers@gaadimatic.com"
                className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-lg transition-all duration-200 hover:bg-white/10"
              >
                <Mail className="mr-2 w-5 h-5" />
                careers@gaadimatic.com
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-white/20">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-primary-100">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  <span>Hyderabad, India</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  <span>+91 99165 00454</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
